import type { CheckboxOnChangeArg, TableColumn } from '@library/types/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Checkbox } from '@library/components/inputs';
import { Link } from 'react-router-dom';
import { formatToCamelCase } from '@bo-utils/common';

export interface StyledTableRowProps {
  headers: Array<TableColumn>;
  rowData: Record<string, unknown>;
  onClick: (id: string) => void;
  onClickCheckbox: ({ id, checked }: { id: string; checked: boolean }) => void;
  isAllChecked: boolean;
  testId?: string;
}

export const StyledTableRow = ({
  headers,
  rowData,
  onClick,
  onClickCheckbox,
  isAllChecked,
  testId = ''
}: StyledTableRowProps) => {
  const [rowChecked, setRowChecked] = useState<boolean>(false);

  const styledRowData = useMemo(() => {
    const result: Array<{
      value: unknown;
      className: string | undefined;
      testId: string;
      link?: boolean;
      getLinks?: (ids: Array<string>) => Array<{ id: string; link: string }>;
    }> = [];

    headers.forEach((header) => {
      if (header.column !== 'checkbox') {
        const value =
          rowData[formatToCamelCase(header.column)] || rowData[header.column];
        result.push({
          value,
          className: header.width,
          testId: `row-${header.column}`,
          link: header.link,
          getLinks: header.getLinks
        });
      }
    });

    return result;
  }, [headers, rowData]);

  useEffect(() => {
    setRowChecked(isAllChecked);
  }, [isAllChecked]);

  const handleOnClick = useCallback(() => {
    onClick(rowData.id as string);
  }, [onClick, rowData.id]);

  const handleCheckboxChange = useCallback(
    ({ id, checked }: CheckboxOnChangeArg) => {
      setRowChecked(checked);
      onClickCheckbox({ id, checked });
    },
    [onClickCheckbox, setRowChecked]
  );

  const renderCells = () => {
    return styledRowData?.map((cell) => {
      if (cell.link && cell.getLinks) {
        const items = cell?.getLinks(cell.value as Array<string>);

        if (!items?.length) {
          return (
            <p
              key={`${cell.testId}-${String(cell.value)}`}
              className={cell.className}
              data-testid={cell.testId}
            >
              -
            </p>
          );
        }
        return (
          <div
            onKeyDown={(e) => {
              e.preventDefault();
            }}
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
            }}
            className={cell.className}
            key={`${cell.testId}-${String(cell.value)}`}
          >
            <div className="flex max-h-[64px] flex-wrap gap-1 overflow-auto">
              {items?.map((item) => (
                <Link
                  className="text-blue-500"
                  data-testid={`${cell.testId}-${item.id}`}
                  to={item.link.replace(',', '').replace(' ', '')}
                  key={`${cell.testId}-${item.id}`}
                >
                  {item.id}
                </Link>
              ))}
            </div>
          </div>
        );
      }

      return (
        <p
          className={cell.className}
          data-testid={cell.testId}
          key={`${cell.testId}-${String(cell.value)}`}
        >
          {cell.value !== undefined && cell.value !== null && cell.value !== ''
            ? String(cell.value)
            : '-'}
        </p>
      );
    });
  };

  return (
    <div className="border-soFresh-140 flex w-full items-center justify-start border-b text-start">
      <p className="w-[3%] px-3">
        <Checkbox
          id={rowData.id as string}
          checked={rowChecked}
          onCheckboxChange={handleCheckboxChange}
        />
      </p>
      <button
        type="button"
        className="border-soFresh-140 text-soClean-110 hover:bg-soFresh-120 text-2xs flex min-h-16 w-[97%] cursor-default items-center text-start xl:text-xs"
        data-testid={testId}
        onClick={handleOnClick}
      >
        {renderCells()}
      </button>
    </div>
  );
};
